/**
 * ============================================================================
 * NewOosDialog — modal for reporting a new OOS event (Phase 7).
 * Owner: Phase 7 — Out-of-Specification Module.
 *
 * Posts to the PG-backed POST /api/v1/oos (drafts/59 OOS-NEW), which inserts a
 * row into oos.oos_events at status REPORTED and writes an OOS_REPORTED history
 * event. On success the workbench refetches so the new row appears in the list
 * (the prior /oos/new hash target was a dead route; the legacy in-memory POST
 * never showed up in the PG-backed list).
 *
 * URS rows: OOS-001 (report), OOS-002 (kind classification).
 * ============================================================================ */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import { buildHeaders, apiUrl } from '../../api/client';
import styles from './OosLog.module.css';
import type { OosKindValue } from './useOosListQuery';

export interface NewOosDialogProps {
  onClose: () => void;
  onCreated: (oosId: string) => void;
}

interface FormState {
  sampleRef: string;
  batchRef: string;
  testMethodRef: string;
  outOfSpecKind: OosKindValue;
  resultValue: string;
  resultUnits: string;
  specLowerBound: string;
  specUpperBound: string;
  specUnits: string;
}

const INITIAL: FormState = {
  sampleRef: '',
  batchRef: '',
  testMethodRef: '',
  outOfSpecKind: 'OUT_OF_SPEC',
  resultValue: '',
  resultUnits: '',
  specLowerBound: '',
  specUpperBound: '',
  specUnits: '',
};

const KIND_VALUES: OosKindValue[] = [
  'OUT_OF_SPEC',
  'OUT_OF_TREND',
  'OUT_OF_EXPECTATION',
];

export function NewOosDialog({ onClose, onCreated }: NewOosDialogProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>();

  function update<K extends keyof FormState>(key: K, v: FormState[K]) {
    setForm((p) => ({ ...p, [key]: v }));
  }

  async function submit() {
    const resultNum = Number(form.resultValue);
    if (
      !form.sampleRef.trim() ||
      !form.testMethodRef.trim() ||
      !form.resultUnits.trim() ||
      form.resultValue.trim() === '' ||
      Number.isNaN(resultNum)
    ) {
      setErrorMsg(
        t('oos.create.missingRequired', {
          defaultValue:
            'Sample reference, test method, and a numeric result value with units are required.',
        }),
      );
      return;
    }
    setSubmitting(true);
    setErrorMsg(undefined);
    try {
      const r = await fetch(apiUrl('/api/v1/oos'), {
        method: 'POST',
        headers: buildHeaders({ json: true }),
        body: JSON.stringify({
          sampleRef: form.sampleRef.trim(),
          batchRef: form.batchRef.trim() || null,
          testMethodRef: form.testMethodRef.trim(),
          outOfSpecKind: form.outOfSpecKind,
          resultValue: resultNum,
          resultUnits: form.resultUnits.trim(),
          specLowerBound:
            form.specLowerBound.trim() === '' ? null : Number(form.specLowerBound),
          specUpperBound:
            form.specUpperBound.trim() === '' ? null : Number(form.specUpperBound),
          specUnits: form.specUnits.trim() || null,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErrorMsg(
          (body as { detail?: string }).detail ??
            t('oos.create.failed', { defaultValue: 'Report OOS failed.' }),
        );
        return;
      }
      const body = (await r.json()) as { id?: string; oos_id?: string };
      onCreated(body.id ?? body.oos_id ?? '');
    } catch (e) {
      setErrorMsg(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.dialogShade} role="dialog" aria-modal="true">
      <div className={styles.dialogBox}>
        <h2 className={styles.dialogTitle}>
          {t('oos.create.title', { defaultValue: 'Report OOS event' })}
        </h2>
        <p className={styles.dialogSubtitle}>
          {t('oos.create.subtitle', {
            defaultValue:
              'Opens a new investigation at REPORTED. FDA OOS Guidance Phase 1 begins once the lab confirms the result.',
          })}
        </p>

        <div className={styles.twoCol}>
          <div className={styles.formField}>
            <label htmlFor="oos-sample">
              {t('oos.create.sampleLabel', { defaultValue: 'Sample reference' })}
            </label>
            <input
              id="oos-sample"
              value={form.sampleRef}
              onChange={(e) => update('sampleRef', e.target.value)}
              placeholder="SAMP-2026-…"
            />
          </div>
          <div className={styles.formField}>
            <label htmlFor="oos-batch">
              {t('oos.create.batchLabel', { defaultValue: 'Batch reference' })}
            </label>
            <input
              id="oos-batch"
              value={form.batchRef}
              onChange={(e) => update('batchRef', e.target.value)}
              placeholder="BATCH-2026-…"
            />
          </div>
        </div>

        <div className={styles.twoCol}>
          <div className={styles.formField}>
            <label htmlFor="oos-method">
              {t('oos.create.methodLabel', { defaultValue: 'Test method' })}
            </label>
            <input
              id="oos-method"
              value={form.testMethodRef}
              onChange={(e) => update('testMethodRef', e.target.value)}
              placeholder="USP <711> / HPLC-…"
            />
          </div>
          <div className={styles.formField}>
            <label htmlFor="oos-kind">
              {t('oos.create.kindLabel', { defaultValue: 'Kind' })}
            </label>
            <select
              id="oos-kind"
              value={form.outOfSpecKind}
              onChange={(e) =>
                update('outOfSpecKind', e.target.value as OosKindValue)
              }
            >
              {KIND_VALUES.map((k) => (
                <option key={k} value={k}>
                  {k.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.twoCol}>
          <div className={styles.formField}>
            <label htmlFor="oos-result">
              {t('oos.create.resultLabel', { defaultValue: 'Result value' })}
            </label>
            <input
              id="oos-result"
              type="number"
              inputMode="decimal"
              value={form.resultValue}
              onChange={(e) => update('resultValue', e.target.value)}
            />
          </div>
          <div className={styles.formField}>
            <label htmlFor="oos-units">
              {t('oos.create.unitsLabel', { defaultValue: 'Result units' })}
            </label>
            <input
              id="oos-units"
              value={form.resultUnits}
              onChange={(e) => update('resultUnits', e.target.value)}
              placeholder="mg/mL, %, CFU/g…"
            />
          </div>
        </div>

        <div className={styles.twoCol}>
          <div className={styles.formField}>
            <label htmlFor="oos-spec-lo">
              {t('oos.create.specLowerLabel', {
                defaultValue: 'Spec lower bound (optional)',
              })}
            </label>
            <input
              id="oos-spec-lo"
              type="number"
              inputMode="decimal"
              value={form.specLowerBound}
              onChange={(e) => update('specLowerBound', e.target.value)}
            />
          </div>
          <div className={styles.formField}>
            <label htmlFor="oos-spec-hi">
              {t('oos.create.specUpperLabel', {
                defaultValue: 'Spec upper bound (optional)',
              })}
            </label>
            <input
              id="oos-spec-hi"
              type="number"
              inputMode="decimal"
              value={form.specUpperBound}
              onChange={(e) => update('specUpperBound', e.target.value)}
            />
          </div>
        </div>

        <div className={styles.formField}>
          <label htmlFor="oos-spec-units">
            {t('oos.create.specUnitsLabel', {
              defaultValue: 'Spec units (optional)',
            })}
          </label>
          <input
            id="oos-spec-units"
            value={form.specUnits}
            onChange={(e) => update('specUnits', e.target.value)}
            placeholder="mg/mL, %, CFU/g…"
          />
        </div>

        {errorMsg && (
          <div className={styles.errorBanner} role="alert">
            {errorMsg}
          </div>
        )}

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>
            {submitting
              ? t('common.submitting', { defaultValue: 'Submitting…' })
              : t('oos.create.submit', { defaultValue: 'Report OOS' })}
          </Button>
        </div>
      </div>
    </div>
  );
}
